// CurvaDeJurosTV.js — versão TV/BROADCAST da CurvaDeJuros (RRTV)
// ============================================================
// Curva de juros (DI1 futuros B3) LIMPA pra vídeo/OBS: sem card/nav.
// Fundo TRANSPARENTE, 1920×1080. Rótulo grande da FORMA (normal/invertida/plana)
// + curto/longo/slope. Pareado com o roteiro make_yield_curve.py.
//
// Rota (app.js):  <Route path="/tv/curva-juros" element={<CurvaDeJurosTV/>} />
// Reusa a MESMA fonte da CurvaDeJuros: /api/go/api/ticker/{DI1FYY}.

import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || '/api/go';
const BLUE = '#3b82f6';
const DI = ['DI1F27','DI1F28','DI1F29','DI1F30','DI1F31','DI1F32','DI1F33','DI1F34',
            'DI1F35','DI1F36','DI1F37','DI1F38','DI1F39','DI1F40','DI1F41'];

export default function CurvaDeJurosTV() {
  const [curve, setCurve] = useState([]);
  const [stats, setStats] = useState({ short: 0, long: 0, slope: 0, daily: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      const rows = await Promise.all(DI.map(async tk => {
        try {
          const r = await fetch(`${API_BASE}/api/ticker/${tk}`);
          if (!r.ok) return null;
          const d = await r.json();
          const rate = d.ult || d.fec || 0;
          if (rate <= 0) return null;
          const yr = 2000 + parseInt(tk.slice(4), 10);
          return { yr, label: `Jan/${tk.slice(4)}`, rate, change: d.var || 0 };
        } catch { return null; }
      }));
      if (!alive) return;
      const clean = rows.filter(Boolean).sort((a, b) => a.yr - b.yr);
      setCurve(clean);
      if (clean.length > 1) {
        const s = clean[0].rate, l = clean[clean.length - 1].rate;
        setStats({ short: s, long: l, slope: (l - s) * 100,
                   daily: clean.reduce((a, c) => a + c.change, 0) / clean.length });
      }
    })();
    return () => { alive = false; };
  }, []);

  const forma = stats.slope > 30 ? 'NORMAL' : stats.slope < -30 ? 'INVERTIDA' : 'PLANA';
  const formaColor = stats.slope > 30 ? '#22c55e' : stats.slope < -30 ? '#ef4444' : BLUE;

  return (
    <div data-forma={forma} data-slope={stats.slope.toFixed(0)}
      style={{ width: '1920px', height: '1080px', background: 'transparent',
               position: 'relative', overflow: 'hidden', color: '#fff',
               fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ position: 'absolute', top: 90, left: 120, right: 120,
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 26, letterSpacing: '.14em',
                        color: BLUE, fontWeight: 700 }}>DI FUTUROS · B3</div>
          <div style={{ fontFamily: "'Roble', Georgia, serif", fontSize: 66, fontWeight: 800,
                        color: '#fff', lineHeight: 1.05, marginTop: 6 }}>Curva de Juros</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 24, color: 'rgba(255,255,255,.6)', letterSpacing: '.1em' }}>FORMATO</div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 60, fontWeight: 800,
                        color: formaColor }}>{forma}</div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 30,
                        color: stats.slope >= 0 ? '#22c55e' : '#ef4444', marginTop: 4 }}>
            {stats.slope >= 0 ? '+' : ''}{stats.slope.toFixed(0)} bps
          </div>
        </div>
      </div>

      {/* A curva */}
      <div style={{ position: 'absolute', left: 80, right: 80, top: 300, height: 560 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={curve} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <defs>
              <linearGradient id="curvaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis dataKey="label" stroke="rgba(255,255,255,0.55)" fontSize={20}
                   interval="preserveStartEnd" minTickGap={80} tickMargin={12} />
            <YAxis stroke="rgba(255,255,255,0.55)" fontSize={20} width={90}
                   domain={['auto', 'auto']} tickFormatter={v => `${v.toFixed(1)}%`} />
            <Area type="monotone" dataKey="rate" stroke={BLUE} strokeWidth={5}
                  fill="url(#curvaGrad)" activeDot={{ r: 8, fill: '#fff' }} isAnimationActive />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Curto · Longo (lower third) */}
      <div style={{ position: 'absolute', bottom: 70, left: 120, right: 120,
                    display: 'flex', gap: 60 }}>
        {curve.length > 0 && [
          ['CURTO', curve[0].label, stats.short],
          ['LONGO', curve[curve.length - 1].label, stats.long],
        ].map(([k, lbl, v]) => (
          <div key={k}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 24,
                          color: 'rgba(255,255,255,.55)', letterSpacing: '.1em' }}>{k} · {lbl}</div>
            <div style={{ fontSize: 56, fontWeight: 800, color: '#fff' }}>{v.toFixed(2)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}
